import { useContext, useState, useMemo, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { BoxFull, Button } from "iuvity-ui-components";
import { AppContext } from "../../context/index";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { InputText } from "iuvity-ui-components/dist/inputtext";
import { TableHeader } from "../datatable/TableHeader";
import { Column } from "primereact/column";
import { FormSkeleton } from "../skeleton/FormSkeleton";
import { FilterMatchMode } from "primereact/api";
import { CustomDialog, DialogProps } from "../dialog/CustomDialog";
import { EmptyTemplate } from "../datatable/EmptyTemplate";
import noResultsImage from "../../assets/Illustration_NoResults.svg";

interface ValidateLabelsTableProps {
  data: any;
  reloadFn: () => void;
}

const formBlankValues = {
    functionality: [],
    key: "",
    esvalue: "",
    envalue: "",
    type: [],
    releaseversion: [],
};

export const ValidateLabelsTable: React.FC<ValidateLabelsTableProps> = ({
  data,
  reloadFn,
}) => {
  const {
    t,
    appState: { language },
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: formBlankValues,
  });

  const { control, setValue } = form;

  const columns = useMemo(() => {
    return [
      {
        field: "id",
        header: "ID",
      },
      {
        field: "functionality",
        header: t("Translations.functionalityLabel"),
      },
      {
        field: "type",
        header: t("Translations.typeLabel"),
      },
      {
        field: "key",
        header: t("Translations.keyLabel"),
      },
      {
        field: "esvalue",
        header: t("Translations.esvalueLabel"),
      },
      {
        field: "envalue",
        header: t("Translations.envalueLabel"),
      },
      {
        field: "releaseversion",
        header: t("Translations.releaseversionLabel"),
      },
    ];
  }, [t]);

  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    functionality: { value: null, matchMode: FilterMatchMode.CONTAINS },
    key: { value: null, matchMode: FilterMatchMode.IN },
    esvalue: { value: null, matchMode: FilterMatchMode.CONTAINS },
    envalue: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type: { value: null, matchMode: FilterMatchMode.CONTAINS },
    releaseversion: { value: null, matchMode: FilterMatchMode.CONTAINS },
  };
  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [filters, setFilters] = useState(defaultFilterValues);
  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const onClearFiltersColumn = (options:any) => {
    setFilters(defaultFilterValues);
    form.reset(formBlankValues);
    options?.filterClearCallback();
    setTimeout(()=>{
      options?.filterClearCallback();
    },100)
  };

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: col.field === 'id'
            ? "ID"
            : t(`Translations.${col.field}Label`),
        }))
      );
  }, [t, language, setSelectedColumns]);

  // ##### CAMBIAR LAS LABELS #####
  const filterTemplate = (options: any) => {
    return (
      <Controller
          name={options.field}
          control={control}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.target.value);
              options.filterCallback(e.target.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-2">
                  {t(`Translations.${options.field}Label`)}
                </label>
                <InputText
                  placeholder={t(`Translations.${options.field}FilterPlaceholder`)} 
                  autoComplete="off"
                  type={'text'}
                  className="mb-2"
                  {...field}
                />
              </>
            );
          }}
      />
    )
  }

  const filterClearTemplate = (options: any) => {
      return (
        <Button
          label={t("ValidationMessages.clearFilterLabel")}
          variant="p-button-text"
          onClick={() => onClearFiltersColumn(options)}
        />
      )
  }
    
  const filterApplyTemplate = (options: any) => {
      return (
        <Button
          label={t("ValidationMessages.applyFilterLabel")}
          className="p-button"
          onClick={()=>{
            options.filterApplyCallback();
          }}
        />
      )
  }

  return (
    <>
      <BoxFull id="validate-labels-table" className={`dt-c mx-0 roles-management-boxfull mb-4`} variant="tertiary" style={{ backgroundColor: 'var(--cardBackgroundColorOne)' }}>
        {data ? (
          <DataTable
            header={
              <TableHeader
              title={t("Translations.translationsTitle")}
              columns={columns}
              selectedColumns={selectedColumns}
              toggleColumns={setSelectedColumns}
              filters={filters}
              setFilters={setFilters}
              reload={reloadFn}
              onClearFiltersColumn={onClearFiltersColumn}
              />
            }
            currentPageReportTemplate={t(
              "ConsoleTranslations.rowsPaginatorTemplateLabel"
            )}
            paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
            emptyMessage={<EmptyTemplate image={noResultsImage} text={t('Roles.emptyDataTableMessage')} />}
            rows={10}
            size="normal"
            filters={filters}
            sortMode={"multiple"}
            dataKey="validatelabelstable-id"
            value={
              data?.map((label: any) => ({
                ...label,
                functionality: label.widgets.join(", "),
                type: t(`Translations.${label.type.toLowerCase()}OptionLabel`),
                esvalue: label?.translations.find((item:any) => item?.languageisocode === "es")?.value,
                envalue: label?.translations.find((item:any) => item?.languageisocode === "en")?.value,
              }))}
            scrollable
            resizableColumns
            globalFilterFields={['id','functionality', 'key', 'esvalue', 'envalue', 'type', 'releaseversion']}
            filterDisplay="menu"
            rowsPerPageOptions={[10,20,50]}
            >
            {selectedColumns.map((col: any) => {
              return (
                <Column
                  key={col.field}
                  className="roles-column"
                  field={col.field}
                  header={col.header}
                  sortable
                  align="left"
                  headerStyle={{
                      maxWidth: "100%",
                      width: "100%",
                  }}
                  style={{
                    maxWidth: "100%",
                    whiteSpace: "normal",
                    width: "155px",
                    minWidth: '160px',
                  }}
                  filterField={col.field}
                  showFilterMatchModes={false}
                  filterMenuStyle={{ width: "23rem" }}
                  filter={["id", "actions","key","esvalue","envalue"].includes(col.field) ? false : true}
                  filterElement={filterTemplate}
                  filterApply={filterApplyTemplate}
                  filterClear={filterClearTemplate}
                  body={(rowData) => <div>{rowData[col.field]}</div>}
                />
                );
              })}
          </DataTable>
        ) : (
          <FormSkeleton length={8} />
          )}
      </BoxFull>
      <CustomDialog
        title={modalProps.title}
        content={modalProps.content}
        visible={modalProps.visible}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
